import { AbstractControl, UntypedFormGroup } from '@angular/forms';

function hasPasswordValidationErrors(control: AbstractControl) {
  if (
    !control ||
    !control.value ||
    control.value.length < 15 ||
    !containsCombinationCharacters(control.value)
  ) {
    return true;
  }

  return false;
}

function containsCombinationCharacters(passwordValue: string) {
  let comboCnt = 0;
  containsLowerCase(passwordValue) ? comboCnt++ : null;
  containsUpperCase(passwordValue) ? comboCnt++ : null;
  containsNumbers(passwordValue) ? comboCnt++ : null;
  containsSpecialCharacters(passwordValue) ? comboCnt++ : null;
  return comboCnt >= 3;
}

function containsLowerCase(str: string) {
  return str.match(/[a-z]/);
}

function containsUpperCase(str: string) {
  return str.match(/[A-Z]/);
}

function containsNumbers(str: string) {
  return str.match(/[0-9]/);
}

function containsSpecialCharacters(str: string) {
  return str.match(/[!@#$%^&*]/);
}

export function passwordStrengthValidator(control: AbstractControl) {
  const hasStrError = hasPasswordValidationErrors(control);

  if (hasStrError) {
    control && control.value && control.markAsTouched();
    return { passwordStrengthError: true };
  }

  return null;
}

export function passwordMismatchValidator(group: UntypedFormGroup) {
  const password = group.get('password');
  const confirmPassword = group.get('confirmPassword');
  let hasMismatch = false;

  // if no control or no control value, nothing to compare for mismatch, do nothing
  if (!password || !confirmPassword || !password.value || !confirmPassword.value) {
    return null;
  }

  // previous non-mismatch error, do nothing
  if (password.invalid && !password.hasError('passwordMismatchError')) {
    return null;
  }

  // previous non-mismatch error, do nothing
  if (confirmPassword.invalid && !confirmPassword.hasError('passwordMismatchError')) {
    return null;
  }

  // previous mismatch, no longer a mismatch, clear error
  if (
    password.invalid &&
    password.hasError('passwordMismatchError') &&
    password.value === confirmPassword.value
  ) {
    password.setErrors(null);
  }

  // previous mismatch, no longer a mismatch, clear error
  if (
    confirmPassword.invalid &&
    confirmPassword.hasError('passwordMismatchError') &&
    password.value === confirmPassword.value
  ) {
    confirmPassword.setErrors(null);
  }

  // previous mismatch, still a mismatch
  if (
    password.invalid &&
    password.hasError('passwordMismatchError') &&
    password.value !== confirmPassword.value
  ) {
    hasMismatch = true;
  }

  // previous mismatch, still a mismatch
  if (
    confirmPassword.invalid &&
    confirmPassword.hasError('passwordMismatchError') &&
    password.value !== confirmPassword.value
  ) {
    hasMismatch = true;
  }

  // newly mismatched
  if (password.valid && confirmPassword.valid && password.value !== confirmPassword.value) {
    hasMismatch = true;
  }

  if (hasMismatch) {
    // set errors at control level
    password.setErrors({ passwordMismatchError: true });
    confirmPassword.setErrors({ passwordMismatchError: true });
    group.markAllAsTouched();
  }

  // if mismatch, set error at form level
  return hasMismatch ? { passwordMismatchError: true } : null;
}

export const PasswordStrengthError =
  'Password must be at least 15 characters and contain at least 3 of the following: lower case (a-z), upper case (A-Z), number (0-9), special characters (! @ # $ % ^ & *)';
export const PasswordMismatchError = 'Passwords must match';
